import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Modal,
  Button,
  TextField,
  InputLabel,
  IconButton,
  Paper,
  Box,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import CloseIcon from "@mui/icons-material/Close";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  CustomStyledButtonSolid,
  StyledTextField,
} from "../../doctor-console/StyledComponents";
import { usePermissions } from "../../login/PermissionContext";
import { ReactComponent as CLoseIcon } from "./CloseButton.svg";
import { ReactComponent as DELETEIcon } from "./DELETEIcon.svg";
import { ReactComponent as EDITIcon } from "./EDITIcon.svg"

/* ──────────────────────────────────────────────────────────────
 *  RoleModalComponent – Save/Reset only under left pane
 * ──────────────────────────────────────────────────────────────*/
export default function RoleModalComponent({ open, onClose }) {
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

  /* ---------- state ---------- */
  // const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  const [roleName, setRoleName] = useState("");
  const [description, setDescription] = useState("");
  const [search, setSearch] = useState("");

  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);

  const [basePermissions, setBasePermissions] = useState({});
  const [permissions, setPermissions] = useState({});

  /* ---------- API setup ---------- */
  const authToken = sessionStorage.getItem("accessToken") ?? "";
  const headers = { Authorization: `Bearer ${authToken}` };
  const { refreshPermissions } = usePermissions();
  useEffect(() => {
    fetchAllRoles();
    fetchModules();
  }, []);

  const fetchModules = () =>
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}roles/module_permissions/`, {
        headers,
      })
      .then(({ data }) => {
        const obj = {};
        (data.data || []).forEach((m) => {
          obj[m.module] = {};
          m.permissions.forEach((p) => {
            const [k, v] = Object.entries(p)[0];
            obj[m.module][k] = v;
          });
        });
        setBasePermissions(obj);
        setPermissions(obj);
      })
      .catch((err) => console.error("fetchModules:", err));

  const fetchAllRoles = () =>
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}roles/`, { headers })
      .then(({ data }) => setRoles(data?.data ?? []))
      .catch((err) => console.error("fetchAllRoles:", err));

  const fetchSingleRole = (id) =>
    axios.get(`${REACT_APP_BACKEND_ROUTE_URL}roles/${id}/`, { headers });

  /* ---------- helpers ---------- */
  const mergeWithBase = (rp = []) => {
    const clone = JSON.parse(JSON.stringify(basePermissions));
    rp.forEach((m) =>
      m.actions.forEach((a) => {
        const [k, v] = Object.entries(a)[0];
        if (!clone[m.module]) clone[m.module] = {};
        clone[m.module][k] = v;
      })
    );
    return clone;
  };

  const populateRoleData = (role) => {
    setSelectedRoleId(role.id);
    setRoleName(role.name);
    setDescription(role.description);
    setPermissions(mergeWithBase(role.permissions));
  };

  /* ---------- UI actions ---------- */
  const handleReset = () => {
    setSelectedRoleId(null);
    setRoleName("");
    setDescription("");
    setPermissions(basePermissions);
  };

  const openRoleEditor = (id) =>
    fetchSingleRole(id)
      .then(({ data }) => {
        populateRoleData(data.data);
        // setOpen(true);
      })
      .catch((err) => console.error("load role:", err));

  const triggerDelete = (r) => {
    setRoleToDelete(r);
    setDeleteOpen(true);
  };

  const confirmDelete = () => {
    if (!roleToDelete) return;
    axios
      .delete(`${REACT_APP_BACKEND_ROUTE_URL}roles/${roleToDelete.id}/`, {
        headers,
      })
      .then((res) => {
        if (res.data.status == "Success") {
          toast.success(res.data.message);
          fetchAllRoles();
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((err) => console.error("delete:", err))
      .finally(() => {
        setDeleteOpen(false);
        setRoleToDelete(null);
        if (roleToDelete?.id === selectedRoleId) handleReset();
      });
  };

  const handlePermissionChange = (m, p) =>
    setPermissions((prev) => ({
      ...prev,
      [m]: { ...prev[m], [p]: !prev[m][p] },
    }));

  const handleModuleToggle = (m) => {
    const allChecked = Object.values(permissions[m]).every(Boolean);
    const upd = {};
    Object.keys(permissions[m]).forEach((k) => (upd[k] = !allChecked));
    setPermissions((prev) => ({ ...prev, [m]: upd }));
  };

  const formatPermissions = () =>
    Object.entries(permissions).map(([module, acts]) => ({
      module,
      actions: Object.entries(acts).map(([n, v]) => ({ [n]: v })),
    }));

  const saveRole = () => {
    const payload = {
      name: roleName,
      description,
      permissions: formatPermissions(),
    };
    const req = selectedRoleId
      ? axios.put(
        `${REACT_APP_BACKEND_ROUTE_URL}roles/${selectedRoleId}/`,
        payload,
        { headers }
      )
      : axios.post(`${REACT_APP_BACKEND_ROUTE_URL}roles/`, payload, {
        headers,
      });

    req
      .then((res) => {
        if (res.data.status == "Success") {
          toast.success(res.data.message);
          fetchAllRoles();
          handleReset();
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((err) => console.error("save:", err));
  };

  /* ---------- derived ---------- */
  const filteredRoles = roles.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase())
  );

  /* ---------- styles ---------- */
  const rootSx = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    boxShadow: 24,
    width: "85vw",
    maxWidth: 1400,
    maxHeight: "95vh",
    outline: 0,
    display: "flex",
    flexDirection: "column",
  };

  const handleClose = () => {
    refreshPermissions();
    onClose();
  };

  /* ---------- render ---------- */
  return (
    <>
      {/* ─────────────────── MAIN MODAL ─────────────────── */}
      {/* <Modal open={open} onClose={() => setOpen(false)}> */}

      <Modal open={open} onClose={handleClose} style={{
        backdropFilter: "blur(5px)",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}>
        <Box sx={{
          width: {
            xs: "100%",
            sm: "80%", // Reduced width to decrease overall size
            md: "80%",
            lg: "60%",
            xl: "50%",
          },
          p: 2,
          mt: 3,
          mb: 3,
          mx: "auto",
          bgcolor: "background.paper",
          borderRadius: 1,
        }}>
          {/* header */}
          <Box
            px={3}
            py={2}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "background.paper",
              p: 1,
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {"Role Management"}
            </Typography>
            <CLoseIcon
              onClick={handleClose}
              style={{
                width: "15px",
                height: "15px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  border: "1px solid #000",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  transform: "scale(1.1)",
                },
              }}
            />
          </Box>
          <Divider />

          {/* body row */}
          <Box
            sx={{ bgcolor: "#ECECEC", p: 2, borderRadius: 1, flexGrow: 1, display: "flex", overflow: "hidden" }}
            px={3}
            py={0}
          >
            {/* LEFT column (form + buttons) */}
            <Box
              width="50%" // Changed to 50% to match right box
              pr={2}
              display="flex"
              flexDirection="column"
              minWidth={0}
              paddingRight={0}
            >
              {/* scrollable form */}
              <Box overflow="auto" flexGrow={1} maxHeight="504px" sx={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#0B0F35',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#0B0F35cc',
                },
              }} >
                <Paper elevation={3} sx={{ p: 2 }}>
                  {/* role & desc */}
                  <Box mb={2}>
                    <InputLabel
                      sx={{
                        minWidth: 100,
                        mr: 1,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                      className="required-label"
                    >
                      Role
                    </InputLabel>
                    <StyledTextField
                      required
                      value={roleName}
                      onChange={(e) => setRoleName(e.target.value)}
                      size="small"
                      sx={{ width: "100%" }}
                    />
                  </Box>
                  <Box mb={2}>
                    <InputLabel
                      sx={{
                        minWidth: 100,
                        mr: 1,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                      className="required-label"
                    >
                      Description
                    </InputLabel>
                    <StyledTextField
                      required
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      multiline
                      rows={2}
                      sx={{
                        width: "100%",
                        "& .MuiInputBase-inputMultiline": {
                          lineHeight: 1.2,
                          padding: "6px 8px",
                        },
                      }}
                    />
                  </Box>

                  <Box mb={2}>
                    <InputLabel
                      sx={{
                        flexBasis: "15%",
                        flexShrink: 0,
                        mr: 2,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      Permissions
                    </InputLabel>
                    <Box overflow="auto"
                      sx={{
                        '&::-webkit-scrollbar': {
                          width: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: '#0B0F35',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: '#0B0F35cc',
                        },
                        flexBasis: "85%",
                        flexGrow: 1,
                        border: 1,
                        borderColor: "#125386",
                        borderRadius: 2,
                        p: 2,
                        // overflowY: "auto",
                        maxHeight: "300px",
                      }}
                    >
                      {Object.entries(permissions).map(([mod, perms]) => {
                        const vals = Object.values(perms);
                        const all = vals.every(Boolean);
                        const some = vals.some(Boolean);

                        return (
                          <Box key={mod} mb={1}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={all}
                                  indeterminate={!all && some}
                                  onChange={() => handleModuleToggle(mod)}
                                />
                              }
                              label={
                                <Typography fontWeight={600}>{mod}</Typography>
                              }
                              sx={{ mb: 0.5 }}
                            />
                            <Box pl={3} display="flex" flexWrap="wrap" gap={1}>
                              {Object.entries(perms).map(([perm, val]) => (
                                <FormControlLabel
                                  key={perm}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={val}
                                      onChange={() =>
                                        handlePermissionChange(mod, perm)
                                      }
                                    />
                                  }
                                  label={
                                    <Typography fontSize={13}>
                                      {perm}
                                    </Typography>
                                  }
                                  sx={{ m: 0 }}
                                />
                              ))}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Paper>
              </Box>

              {/* Save / Reset bar (only left) */}
              <Divider />
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ width: 2, mx: 1 }}
            />

            {/* RIGHT column (roles list) */}
            <Box width="50%" pl={2} display="flex" flexDirection="column" bgcolor="#FFFFFF">
              <Divider sx={{ mb: 1 }} />
              <Box mb={1}>
                <InputLabel
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  Search Roles
                </InputLabel>
                <StyledTextField
                  placeholder="Search roles…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box overflow="auto" flexGrow={1} mt={1} maxHeight="400px" sx={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#0B0F35',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#0B0F35cc',
                },
              }}>
                {filteredRoles.map((role, i) => (
                  <React.Fragment key={role.id}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      py={1}
                      px={1.5}
                      sx={{
                        cursor: "pointer",
                        backgroundColor:
                          role.id === selectedRoleId
                            ? "rgba(33,205,192,0.15)"
                            : "transparent",
                        borderLeft:
                          role.id === selectedRoleId
                            ? "4px solid #21cdc0"
                            : "4px solid transparent",
                        borderRadius: 1,
                      }}
                      onClick={() => populateRoleData(role)}
                    >
                      <Typography
                        fontSize={14}
                        fontWeight={400}
                        sx={{
                          color:
                            role.id === selectedRoleId ? "#21cdc0" : "#000",
                        }}
                      >
                        {role.name}
                      </Typography>
                      <Box onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', gap: '16px' }}>
                          <IconButton
                            size="small"
                            onClick={() => openRoleEditor(role.id)}
                            sx={{
                              border: '0.5px solid #0E3F66',
                              boxShadow: '0px 1px 5.2px 0px #00000040 inset',
                              background: '#EEEEEE'
                            }}
                          >
                            <EDITIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => triggerDelete(role)}
                            sx={{
                              border: '0.5px solid #0E3F66',
                              boxShadow: '0px 1px 5.2px 0px #00000040 inset',
                              background: '#EEEEEE'
                            }}
                          >
                            <DELETEIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                    {i < filteredRoles.length - 1 && (
                      <Divider sx={{ bgcolor: "primary.main", opacity: 0.5 }} />
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </Box>
          <Divider />

          {/* Button row */}
          <Box display="flex" justifyContent="space-between" px={3} py={2}>
            <Stack direction="row" spacing={2}>
              <button
                onClick={handleReset}
                className="submit-button"
                style={{ backgroundColor: "#0E3F66", padding: "6px 16px", borderRadius: "10px", color: "#FFFFFF", boxShadow: "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)" }} // Added white text
              >
                RESET
              </button>
              <button
                onClick={saveRole}
                disabled={!roleName.trim() || !description.trim()}
                className="submit-button"
                style={{
                  backgroundColor: "#0E3F66",
                  padding: "6px 16px",
                  borderRadius: "10px",
                  color: "#FFFFFF", // Added white text
                  boxShadow: "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
                  // cursor: "not-allowed", // Disabled pointer on hover when disabled
                  "&:hover:disabled": {
                    cursor: "not-allowed",
                  }
                }}
              >
                SAVE
              </button>
            </Stack>
            <button
              onClick={handleClose}
              className="button button-contained-cancel"
              style={{ backgroundColor: "#FF3B30", color: "#FFF", padding: "6px 16px", borderRadius: "10px" }} // Matched size with other buttons
            >
              CLOSE
            </button>
          </Box>
        </Box>
      </Modal >

      {/* ───── delete confirm ───── */}
      < Modal open={deleteOpen} onClose={() => setDeleteOpen(false)
      }>
        <Box sx={{ ...rootSx, width: 380 }}>
          <Box px={3} py={2}>
            <Typography fontSize={16} mb={2}>
              Delete “{roleToDelete?.name}”?
            </Typography>
            <Box display="flex" justifyContent="flex-end">
              <Button
                onClick={() => setDeleteOpen(false)}
                variant="outlined"
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button onClick={confirmDelete} color="error" variant="contained">
                Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal >
    </>
  );
}
